import "./StandardPreviewCard.css";
import { useEffect, useMemo } from "react";
import { Card, CardActionArea } from "@mui/material";
import WebFont from "webfontloader";

function StandardPreviewCard({ index, cardData, userData, onClick }) {
  const fontsConfig = useMemo(() => {
    if (!cardData || Object.keys(cardData).length <= 0) return null;

    const frontFonts = cardData?.view_data?.id_front?.text_objects ?? [];
    const backFonts =
      cardData?.type === 2
        ? cardData?.view_data?.id_back?.text_objects ?? []
        : [];
    const collectedFamilies = [...frontFonts, ...backFonts]
      .map((font) => font?.fontFamily?.split(",")[0]?.trim())
      .filter(Boolean);

    if (!collectedFamilies.length) return null;

    const uniqueFamilies = Array.from(new Set(collectedFamilies));
    return uniqueFamilies.map((font) => ({
      family: font,
      weights: [400, 600, 700],
    }));
  }, [cardData]);

  const fontFamilies = useMemo(() => {
    if (!fontsConfig || fontsConfig.length === 0) {
      return ["Roboto:400,600"];
    }

    return fontsConfig.map(({ family, weights }) => {
      const uniqueWeights = Array.from(new Set(weights ?? [400]));
      return `${family}:${uniqueWeights.join(",")}`;
    });
  }, [fontsConfig]);

  useEffect(() => {
    if (!fontFamilies?.length) return;

    WebFont.load({
      google: {
        families: fontFamilies,
      },
    });
  }, [fontFamilies]);

  const textTransformSelector = (type) => {
    let textType = null;

    switch (type) {
      case "camelcase":
        return (textType = "capitalize");

      case "capitalize":
        return (textType = "uppercase");

      default:
        return (textType = "inherit");
    }
  };

  // Helper function to determine if an image is a signature
  const isSignature = (img) => {
    return (
      img?.signature ||
      img?.tag?.toLocaleLowerCase() === "sign" ||
      img?.tag?.toLocaleLowerCase() === "signature"
    );
  };

  // Helper function to determine objectFit for images
  const getObjectFit = (img) => {
    // Use objectFit from img object if explicitly set (allows override from PreviewPage)
    if (img?.objectFit) return img.objectFit;
    if (img?.adminImage) return "contain";
    if (isSignature(img)) return "contain";
    return "cover";
  };

  return (
    <Card className="standardPreviewCard">
      <CardActionArea style={{ overflowX: "auto" }}>
        <div
          id={`previewcustomCard-${index}`}
          className="previewcustomCard_cont"
          onClick={onClick ? onClick : () => {}}
        >
          {/* FRONT CARD */}
          <section
            className="previewcustomCard_front"
            style={{
              background: `url(${cardData?.document_path_front}) no-repeat center/contain`,
              height: cardData?.view_data?.id_front?.height + "px",
              width: cardData?.view_data?.id_front?.width + "px",
              position: "relative",
              margin: "auto",
            }}
          >
            {cardData?.view_data?.id_front?.image_objects?.map((img, i) => {
              if (img?.adminImage === true && img?.signature === true) {
                return null;
              }
              // Don't show teacher_image if user is student
              if (img?.teacher_image === true && userData?.role === "student") {
                return null;
              }

              return (
                <img
                  key={i}
                  src={userData ? userData?.user_details?.[img.tag] : img?.src}
                  style={{
                    ...img,
                    position: "absolute",
                    border: `${img?.strokeWidth}px solid ${img?.stroke}`,
                    objectFit: getObjectFit(img),
                    backgroundColor: img?.background_fill || "transparent",
                  }}
                />
              );
            })}
            {cardData?.view_data?.id_front?.text_objects?.map((t, i) => (
              <div
                key={i}
                style={{
                  ...t,
                  position: "absolute",
                  fontWeight: t?.fontStyle,
                  textTransform: textTransformSelector(t?.text_format),
                }}
              >
                <p style={{ color: t?.fill, margin: 0 }}>
                  {t?.prefix && `${t.prefix} `}
                  {userData ? (
                    <>{userData?.user_details?.[t?.tag]}</>
                  ) : (
                    <>{t.text}</>
                  )}
                </p>
              </div>
            ))}
          </section>

          {/* BACK CARD */}
          {cardData?.type == 2 && (
            <section
              className="previewcustomCard_back"
              style={{
                background: `url(${cardData?.document_path_back}) no-repeat center/contain`,
                height: cardData?.view_data?.id_back?.height + "px",
                width: cardData?.view_data?.id_back?.width + "px",
                position: "relative",
                margin: "auto",
                marginTop: 20,
              }}
            >
              {cardData?.view_data?.id_back?.image_objects?.map((img, i) => {
                if (img?.adminImage === true && img?.signature === true) {
                  return null;
                }
                // Don't show teacher_image if user is student
                if (
                  img?.teacher_image === true &&
                  userData?.role === "student"
                ) {
                  return null;
                }

                return (
                  <img
                    key={i}
                    src={
                      userData ? userData?.user_details?.[img.tag] : img?.src
                    }
                    style={{
                      ...img,
                      position: "absolute",
                      border: `${img?.strokeWidth}px solid ${img?.stroke}`,
                      objectFit: getObjectFit(img),
                      backgroundColor: img?.background_fill || "transparent",
                    }}
                  />
                );
              })}
              {cardData?.view_data?.id_back?.text_objects?.map((t, i) => (
                <div
                  key={i}
                  style={{
                    ...t,
                    position: "absolute",
                    fontWeight: t?.fontStyle,
                    textTransform: textTransformSelector(t?.text_format),
                  }}
                >
                  <p style={{ color: t?.fill, margin: 0 }}>
                    {t?.prefix && `${t.prefix} `}
                    {userData ? (
                      <>{userData?.user_details?.[t?.tag]}</>
                    ) : (
                      <>{t.text}</>
                    )}
                  </p>
                </div>
              ))}
            </section>
          )}
        </div>
      </CardActionArea>
    </Card>
  );
}

export default StandardPreviewCard;
